import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useApiContext } from 'context/ApiContext';
import { useGlobalContext } from 'context/GlobalContext';
import { KEYS } from 'helpers/react-query.config';
import { CreateProblemFlowRequest } from 'api/process';

/**
 * Hook para crear un nuevo flujo de estatus de Problemas.
 *
 * Realiza una mutación a la API para crear un flujo de estatus de Problemas.
 * Maneja el éxito y los errores mostrando mensajes al usuario.
 *
 * @returns {object} Objeto con la función para crear el flujo de estatus de Problemas y el estado de carga.
 * @example
 * const { createNewStatus, isCreateNewStatusLoading } = useCreateNewStatus();
 */
const useCreateNewStatus = () => {
  const { showApiError, showSuccess } = useGlobalContext();
  const { processApi } = useApiContext();
  const { t } = useTranslation('global');
  const queryClient = useQueryClient();

  return useMutation(
    (data: CreateProblemFlowRequest) =>
      processApi.ProblemFlow.createNewStatus(data),
    {
      onError: (error) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
      },
      onSuccess: async () => {
        showSuccess(t('problems.statusFlows.messages.createSuccess'));
        await queryClient.invalidateQueries([
          KEYS.process.problemFlow.generalSearch()
        ]);
      }
    }
  );
};

export default useCreateNewStatus;
